/* eslint-disable @typescript-eslint/no-explicit-any */
/// <reference types="@types/google.maps" />
import React, { Component, useEffect, useRef, useCallback, useState, ErrorInfo, ReactNode } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useHumanSignal } from '../hooks/useHumanSignal';
import { getStateMapCenter, DEFAULT_MAP_ZOOM } from '../lib/stateRoutes';
import { DEFAULT_STATE } from '../lib/stateCore.mjs';
import { Plus, Minus } from 'lucide-react';

// Google Maps initialization is now handled globally in App.tsx

// Error boundary to catch errors in GoogleMapView
interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class MapErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    declare readonly props: Readonly<ErrorBoundaryProps>;
    declare state: ErrorBoundaryState;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[GoogleMapView Error]:', error);
        console.error('[GoogleMapView ErrorInfo]:', errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="w-full h-full flex items-center justify-center bg-surface-canvas text-gray-400 p-4">
                    <div className="text-center">
                        <p className="font-medium text-gray-500">Map failed to load</p>
                        <p className="text-xs mt-2 uppercase tracking-wider">{this.state.error?.message || 'Unknown error'}</p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// Gym data structure for map markers
export interface MapGym {
    gymId: string;
    lat: number;
    lng: number;
    gymName: string;
}

interface GoogleMapViewProps {
    gyms: MapGym[];
    selectedGymId: string | null;
    onSelectGymId: (gymId: string | null) => void;
    hoveredGymId?: string | null;
    onHoverChange?: (gymId: string | null) => void;
    enableClustering?: boolean;
    isMobile?: boolean;
    // Unique key for current search - when this changes, bounds will be re-fitted
    searchKey?: string;
    center?: { lat: number; lng: number };
    zoom?: number;
    onIdle?: () => void;
    showMarkers?: boolean;
    triggerResize?: any;
    radius?: number;
    shouldLoadMaps?: boolean;
}

// UI-25: no local centre constant — the caller passes `center` (route-state-
// derived via getStateMapCenter), and the init fallback below uses the shared
// table's default-state entry. The former per-file NSW_CENTER copy could drift
// from DirectoryPage's independently.
const SINGLE_GYM_ZOOM = 15;

// Create marker icon URL (SVG as data URL for regular markers)
// Note: must pass googleObj since google is not available at module load time
const createMarkerIcon = (googleObj: any, state: 'default' | 'hover' | 'selected') => {
    // Map marker uses brand-primary's hex value (#2F5D9F) for the default and
    // hover states (hover differs by size only). The hex is repeated literally
    // because Google Maps marker config requires a literal colour value — it
    // cannot read from Tailwind tokens or CSS custom properties. If brand-primary
    // changes, update both entries.
    // Fills MUST be opaque: the hover/selection pins are dedicated overlay
    // markers drawn ON TOP of the default marker, so a translucent fill lets
    // the default pin show through and reads as two stacked icons.
    // Selected state uses #DC2626 (the favourite-active hover red; its token was
    // pruned as unreferenced in UI-14 — this literal is the surviving record).
    // Same Google Maps API constraint applies: hex is repeated literally.
    const colors = {
        default: { fill: '#2F5D9F', size: 38, scale: 1 },
        hover: { fill: '#2F5D9F', size: 46, scale: 1.2 },
        selected: { fill: '#DC2626', size: 46, scale: 1.2 }
    };

    const { fill, scale } = colors[state];

    const svg = `<svg width="38" height="38" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
            fill="${fill}"/>
      <circle cx="12" cy="9" r="2.5" fill="white"/>
    </svg>`;

    return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new googleObj.maps.Size(38 * scale, 38 * scale),
        anchor: new googleObj.maps.Point(19 * scale, 38 * scale)
    };
};

// Google maps global is available via CDN but referenced via googleObj mostly.
// This empty export helps avoid "unused" warning while keeping types available.
export { };


const GoogleMapViewInner: React.FC<GoogleMapViewProps> = ({
    gyms,
    selectedGymId,
    onSelectGymId,
    hoveredGymId,
    onHoverChange,
    enableClustering = false,
    isMobile = false,
    searchKey = '',
    center,
    zoom,
    onIdle,
    showMarkers = true,
    triggerResize,
    radius,
    shouldLoadMaps = false
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const googleRef = useRef<any>(null);
    // R-03: billable map creation additionally waits for the first trusted input
    // event. Crawlers render JS but dispatch none, so bot renders of the 1,434
    // map-bearing routes stop instantiating maps; humans fire one within moments.
    const humanSeen = useHumanSignal();

    // Track which searchKey we've already fitted bounds for
    // Only refit when searchKey changes (new search), not when gyms/coords update
    const fittedForSearchKeyRef = useRef<string>('');

    // Initialize Google Maps
    useEffect(() => {
        if (!shouldLoadMaps || !humanSeen) return;

        const apiKey = (process.env as Record<string, string>).GOOGLE_MAPS_JS_KEY;

        if (!apiKey) {
            setLoadError('Google Maps API key not configured');
            console.error('GOOGLE_MAPS_JS_KEY not found in environment');
            return;
        }

        const initMap = async () => {
            try {
                // Script initialization is now handled globally in App.tsx
                // Import the maps library
                const mapsLib = await importLibrary('maps') as any;
                const { Map } = mapsLib;

                // Ensure isLoaded is true even if it was false on mount
                setIsLoaded(true);

                if (!containerRef.current) {
                    console.error('[GoogleMapView] Container ref is null');
                    return;
                }

                // Store google reference
                googleRef.current = (window as any).google;

                const map = new Map(containerRef.current, {
                    // UI-25: prefer the caller's route-state-derived centre; the
                    // fallback (no prop) is the shared table's default-state entry.
                    center: center ?? getStateMapCenter(DEFAULT_STATE),
                    zoom: DEFAULT_MAP_ZOOM,
                    disableDefaultUI: true,
                    zoomControl: false,
                    gestureHandling: 'greedy',
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi",
                            elementType: "geometry",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "transit",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "road",
                            elementType: "labels.icon",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "administrative.land_parcel",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                });

                map.addListener('click', () => {
                    onSelectGymId(null);
                });

                mapRef.current = map;
                setIsLoaded(true);
            } catch (err) {
                console.error('[GoogleMapView] Failed to load Google Maps:', err);
                setLoadError('Failed to load Google Maps: ' + (err as Error).message);
            }
        };

        initMap();

        const currentMarkers = markersRef.current;
        const currentClusterer = clustererRef.current;

        return () => {
            // Cleanup markers
            currentMarkers.forEach(marker => {
                if (marker && typeof marker.setMap === 'function') {
                    marker.setMap(null);
                }
            });
            currentMarkers.clear();

            // Cleanup clusterer
            if (currentClusterer) {
                currentClusterer.clearMarkers();
            }

            mapRef.current = null;
            // Reset load/fit state so a later shouldLoadMaps false→true cycle
            // re-runs the isLoaded-gated effects (markers, center/zoom, bounds
            // fit) against the re-created map. Without this, setIsLoaded(true)
            // on re-init is a no-op and the new map stays empty at the default
            // NSW view.
            fittedForSearchKeyRef.current = '';
            setIsLoaded(false);
        };
    }, [shouldLoadMaps, humanSeen]);

    // Manage markers - create/remove markers and set up clustering
    // Note: onSelectGymId is removed from deps to avoid recreating everything on callback changes
    // (We update listener via surgical effect or just keep it since it's most likely stable now)
    const lastGymsRef = useRef<string>('');
    const lastShowMarkersRef = useRef<boolean>(true);
    useEffect(() => {
        const map = mapRef.current;
        const googleObj = googleRef.current;
        if (!map || !isLoaded || !googleObj) return;

        const gymsSignature = gyms.map(g => `${g.gymId}:${g.lat}:${g.lng}`).join('|');
        const gymsChanged = lastGymsRef.current !== gymsSignature;
        const showMarkersChanged = lastShowMarkersRef.current !== showMarkers;

        // Only proceed with heavy updates if gyms or showMarkers state changed
        if (!gymsChanged && !showMarkersChanged && markersRef.current.size === gyms.length) {
            return;
        }

        lastShowMarkersRef.current = showMarkers;

        if (!showMarkers) {
            // Optimization: hide everything and stop early
            markersRef.current.forEach(m => m.setMap(null));
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
                clustererRef.current = null;
            }
            lastGymsRef.current = ''; // Reset so they are re-added next time
            return;
        }

        lastGymsRef.current = gymsSignature;

        const currentIds = new Set(gyms.map(g => g.gymId));

        // Remove markers for gyms no longer in list
        markersRef.current.forEach((marker, id) => {
            if (!currentIds.has(id)) {
                if (marker && typeof marker.setMap === 'function') {
                    marker.setMap(null);
                }
                markersRef.current.delete(id);
            }
        });

        // Clear existing clusterer
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
            clustererRef.current = null;
        }

        // Add or update markers
        const newMarkers: any[] = [];

        gyms.forEach(gym => {
            let marker = markersRef.current.get(gym.gymId);

            if (!marker) {
                // Create new regular Marker (not AdvancedMarkerElement)
                marker = new googleObj.maps.Marker({
                    position: { lat: gym.lat, lng: gym.lng },
                    map: null, // Don't add to map yet if clustering
                    title: gym.gymName,
                    icon: createMarkerIcon(googleObj, 'default')
                }) as google.maps.Marker;

                // Click handler
                marker.addListener('click', () => {
                    onSelectGymId(gym.gymId);
                });

                // Hover handlers
                marker.addListener('mouseover', () => {
                    if (onHoverChange) onHoverChange(gym.gymId);
                });

                marker.addListener('mouseout', () => {
                    if (onHoverChange) onHoverChange(null);
                });

                markersRef.current.set(gym.gymId, marker);
            }

            newMarkers.push(marker);
        });

        // Apply clustering if enabled or > 50 gyms
        const shouldCluster = enableClustering || gyms.length > 50;

        if (showMarkers && shouldCluster && newMarkers.length > 0) {
            clustererRef.current = new MarkerClusterer({
                map,
                markers: newMarkers,
                onClusterClick: (_event, cluster, map) => {
                    const bounds = cluster.bounds;
                    if (bounds) {
                        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
                    } else {
                        const position = cluster.position;
                        if (position) {
                            map.setCenter(position);
                            map.setZoom((map.getZoom() || 10) + 2);
                        }
                    }
                }
            });
        } else if (showMarkers) {
            // Add markers directly to map
            newMarkers.forEach(marker => {
                marker.setMap(map);
            });
        } else {
            newMarkers.forEach(marker => {
                marker.setMap(null);
            });
            // NOTE: any existing clusterer is already torn down unconditionally at the
            // top of this effect (the "Clear existing clusterer" block), so there is no
            // clusterer to clear here — a guard at this point is provably dead (clustererRef
            // .current is always null on this path) and was removed for strictNullChecks.
        }
    }, [gyms, isLoaded, enableClustering, showMarkers, onHoverChange]);

    // Dedicated markers for hover and selection to avoid touching clusterer-managed markers
    const selectionMarkerRef = useRef<google.maps.Marker | null>(null);
    const hoverMarkerRef = useRef<google.maps.Marker | null>(null);

    // Initialize/Cleanup markers
    useEffect(() => {
        return () => {
            if (selectionMarkerRef.current) selectionMarkerRef.current.setMap(null);
            if (hoverMarkerRef.current) hoverMarkerRef.current.setMap(null);
            selectionMarkerRef.current = null;
            hoverMarkerRef.current = null;
        };
    }, []);

    // Update highlights surgically via dedicated overlay markers
    useEffect(() => {
        const map = mapRef.current;
        const googleObj = googleRef.current;
        if (!map || !googleObj || !isLoaded) return;

        // Manage Selection Marker (Red)
        if (!showMarkers) {
            if (selectionMarkerRef.current) selectionMarkerRef.current.setMap(null);
            if (hoverMarkerRef.current) hoverMarkerRef.current.setMap(null);
            return;
        }

        // ALWAYS show selection marker if selected, regardless of hover
        const selectedGym = selectedGymId ? gyms.find(g => g.gymId === selectedGymId) : null;
        if (selectedGym) {
            const icon = createMarkerIcon(googleObj, 'selected');
            const position = { lat: selectedGym.lat, lng: selectedGym.lng };

            if (!selectionMarkerRef.current) {
                selectionMarkerRef.current = new googleObj.maps.Marker({
                    position, icon, map, zIndex: 999, clickable: false, optimized: false
                });
            } else {
                selectionMarkerRef.current.setOptions({ position, icon, map, zIndex: 999 });
            }
        } else {
            if (selectionMarkerRef.current) selectionMarkerRef.current.setMap(null);
        }

        // 2. Manage Hover Marker (Blue)
        // ONLY show hover marker if NOT selected (selection takes visual priority)
        const hoveredGym = hoveredGymId ? gyms.find(g => g.gymId === hoveredGymId) : null;
        if (hoveredGym && hoveredGymId !== selectedGymId) {
            const icon = createMarkerIcon(googleObj, 'hover');
            const position = { lat: hoveredGym.lat, lng: hoveredGym.lng };

            if (!hoverMarkerRef.current) {
                hoverMarkerRef.current = new googleObj.maps.Marker({
                    position, icon, map, zIndex: 1000, clickable: false, optimized: false
                });
            } else {
                hoverMarkerRef.current.setOptions({ position, icon, map, zIndex: 1000 });
            }
        } else {
            if (hoverMarkerRef.current) hoverMarkerRef.current.setMap(null);
        }
    }, [selectedGymId, hoveredGymId, isLoaded, gyms, showMarkers]);

    // Handle viewport based on gyms
    useEffect(() => {
        const map = mapRef.current;
        const googleObj = googleRef.current;
        if (!map || !isLoaded || !googleObj || !showMarkers) return;

        // Skip if no gyms (don't reset view)
        if (gyms.length === 0) return;

        // Create a signature to detect when to fit bounds
        // Inclusion of radius, coordinates, and visibility ensures re-fits when:
        // 1. Location/SearchKey changes
        // 2. Gym coordinates are updated (from suburb fallback to real locations)
        // 3. Radius changes
        // 4. Selection is cleared (transition from 'selected' back to 'none')
        // DL-9: isVisible is intentionally NOT in the signature — a hidden→visible
        // flip (SPA nav away and back to /gyms) must not re-fit/re-frame the map.
        // Tile/layout correction on becoming visible is handled by the resize
        // effect below (via triggerResize), without re-framing.
        const gymIdsSignature = gyms.map(g => `${g.gymId}:${g.lat}:${g.lng}`).sort().join(',');
        const selectionState = selectedGymId ? 'selected' : 'none';
        const currentSignature = `${searchKey || 'default'}:${gymIdsSignature}:${radius || 0}:${showMarkers}:${selectionState}`;

        if (fittedForSearchKeyRef.current === currentSignature) {
            return;
        }

        // IMPORTANT: If we are in 'selected' state, we don't perform a group fit-bounds.
        // We let the panTo effect handle it. We only want to trigger a FIT when 
        // transitioning back to 'none' or when the search results themselves change.
        if (selectedGymId && fittedForSearchKeyRef.current.includes(':none')) {
            // We've already fitted for the group, now we are just selecting.
            // Update ref so we don't fit-bounds on top of the pan, but only if we had a group fit before.
            fittedForSearchKeyRef.current = currentSignature;
            return;
        }

        fittedForSearchKeyRef.current = currentSignature;

        // Force a resize check before fitting bounds (helps with hidden -> visible transitions)
        googleObj.maps.event.trigger(map, 'resize');

        const performFit = () => {
            if (gyms.length === 1) {
                map.setCenter({ lat: gyms[0]!.lat, lng: gyms[0]!.lng }); // safe: length === 1 guarantees index 0
                map.setZoom(SINGLE_GYM_ZOOM);
            } else if (gyms.length > 1) {
                const bounds = new googleObj.maps.LatLngBounds();
                gyms.forEach(gym => {
                    bounds.extend({ lat: gym.lat, lng: gym.lng });
                });

                if (!bounds.isEmpty()) {
                    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });

                    // Zoom Correction: Ensure we don't get stuck in "World View" (Zoom < 5)
                    // We check after idle to ensure the transition is complete.
                    const correctionListener = googleObj.maps.event.addListener(map, 'idle', () => {
                        const currentZoom = map.getZoom();
                        if (currentZoom !== undefined && currentZoom < 5 && gyms.length > 0) {
                            console.warn('[GoogleMapView] FitBounds resulted in world-view. Correcting zoom.', {
                                currentZoom,
                                gymCount: gyms.length,
                                searchKey
                            });

                            let fallbackZoom = 10;
                            if (radius) {
                                if (radius <= 1) fallbackZoom = 15;
                                else if (radius <= 2) fallbackZoom = 14;
                                else if (radius <= 5) fallbackZoom = 13;
                                else if (radius <= 10) fallbackZoom = 12;
                                else if (radius <= 25) fallbackZoom = 11;
                            }
                            map.setZoom(fallbackZoom);
                        }
                        // Always remove the listener after first idle to avoid zoom fights
                        googleObj.maps.event.removeListener(correctionListener);
                    });
                }
            }
        };

        // Use a small timeout to allow layout shifts (like Directory reveals) to settle
        const timer = setTimeout(performFit, 100);
        return () => clearTimeout(timer);
    }, [gyms, isLoaded, searchKey, showMarkers, radius, triggerResize]);

    // Force resize trigger
    useEffect(() => {
        const map = mapRef.current;
        const googleObj = googleRef.current;
        if (map && googleObj && isLoaded) {
            googleObj.maps.event.trigger(map, 'resize');
        }
    }, [triggerResize, isLoaded]);

    // Handle manual center/zoom overrides (e.g. initial search before hydration)
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isLoaded) return;

        if (center) {
            map.setCenter(center);
        }
        if (zoom !== undefined) {
            map.setZoom(zoom);
        }
    }, [center, zoom, isLoaded]);

    // Pan to selected gym (desktop only - mobile keeps map stationary)
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isLoaded || !selectedGymId || isMobile) return;

        const selectedGym = gyms.find(g => g.gymId === selectedGymId);
        if (selectedGym) {
            map.panTo({ lat: selectedGym.lat, lng: selectedGym.lng });
        }
    }, [selectedGymId, gyms, isLoaded, isMobile]);

    // Zoom controls
    const handleZoomIn = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.setZoom((mapRef.current.getZoom() || 10) + 1);
        }
    }, []);

    const handleZoomOut = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.setZoom((mapRef.current.getZoom() || 10) - 1);
        }
    }, []);

    // Listen for map idle event
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !onIdle || !isLoaded) return;

        const listener = map.addListener('idle', () => {
            onIdle();
        });

        return () => {
            google.maps.event.removeListener(listener);
        };
    }, [isLoaded, onIdle]);

    // DL-5: a failed map never fires 'idle', so signal onIdle once on load error
    // to release the reveal (isMapMoving) immediately instead of leaving the list
    // stuck under a blur until the 6s fallback fires.
    useEffect(() => {
        if (loadError && onIdle) onIdle();
    }, [loadError, onIdle]);

    if (loadError) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-surface-canvas text-gray-500">
                <p>{loadError}</p>
            </div>
        );
    }

    return (
        // The map canvas + Marker objects are not exposed to assistive tech, so
        // label the wrapper as a landmark region; the gym result list serves as
        // the equivalent text alternative for AT users.
        <div role="region" aria-label="Gym locations map" className="relative w-full h-full">
            <div ref={containerRef} className="absolute inset-0" />

            {/* Loading overlay - fade out when maps loaded */}
            <div className={`
                absolute inset-0 flex items-center justify-center bg-surface-canvas z-[10]
                transition-opacity duration-300 pointer-events-none
                ${isLoaded ? 'opacity-0' : 'opacity-100'}
            `}>
                <div className="animate-pulse text-gray-400 font-medium">Loading map...</div>
            </div>

            {/* Controls */}
            {isLoaded && (
                <div className="absolute bottom-6 right-3 z-[1000] flex flex-col gap-2">
                    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                        <button
                            onClick={handleZoomIn}
                            className="p-2 hover:bg-gray-50 border-b border-gray-200 block"
                            title="Zoom in"
                            aria-label="Zoom in"
                        >
                            <Plus size={18} className="text-slate-600" />
                        </button>
                        <button
                            onClick={handleZoomOut}
                            className="p-2 hover:bg-gray-50 block"
                            title="Zoom out"
                            aria-label="Zoom out"
                        >
                            <Minus size={18} className="text-slate-600" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Wrapped component with error boundary
const GoogleMapViewWithErrorBoundary: React.FC<GoogleMapViewProps> = (props) => (
    <MapErrorBoundary>
        <GoogleMapViewInner {...props} />
    </MapErrorBoundary>
);

export default GoogleMapViewWithErrorBoundary;
